import { useEffect, useState } from "react";
import * as E from "@numi/engine";
import { S, useApp, go, touch, prog, saveProg, queue, addStars, calmMode, updateChild, onLeave, pushRecent } from "../store.ts";
import { Card, Html, Icon, burstAt } from "../ui.tsx";
import { SFX, canSpeak, speak } from "../sound.ts";
import { sayAloud } from "../question.ts";
import { completeRunningStep } from "../plan.ts";
import { tapCount } from "./Question.tsx";

const SECS = 60, CALM_GOAL = 10;
const CS = ["var(--t-orange)", "var(--t-sky)", "var(--t-leaf)", "var(--t-pink)"];

function nextBub() {
  const b = S.bub!; const tid = b.mixed ? (b.pool?.length ? E.pick(b.pool) : E.pick(E.topicsFor(S.child!.grade)).id) : b.topic!;
  const q = E.topic(tid)!.gen(prog(tid).level); q.tid = tid; b.tid = tid; b.q = q; b.opts = E.bubbleOptions(q);
}
/** `pool` limits a mixed game to some topics (e.g. only Vedic tricks); `label` names them in the footer note. */
export function startBubbles(mixed: boolean, pool: string[] | null = null, label: string | null = null) {
  S.bub = { pool, label, mixed, calm: calmMode(), score: 0, end: Date.now() + SECS * 1000, q: null as any, opts: [], topic: mixed ? null : S.topic, tid: "", starsWon: 0 };
  nextBub(); if (mixed) S.topic = S.topic ?? null; go("bubbles");
}
function finish() {
  const b = S.bub!; SFX.up();
  // A Bubble Pop that is a step of today's lesson ticks that step off.
  if (S.planRunning != null && S.plan?.steps[S.planRunning]?.kind === "bubbles") completeRunningStep();
  S.result = { kind: "bubble", calm: b.calm, score: b.score, stars: b.starsWon, sticker: S.pendingSticker, mixed: b.mixed }; S.pendingSticker = null; go("done");
}

export function Bubbles() {
  const s = useApp(); const b = s.bub!; const q = b.q; const [left, setLeft] = useState(SECS * 1000); const [popped, setPopped] = useState<string | null>(null); const [wrong, setWrong] = useState<string | null>(null);
  useEffect(() => {
    if (b.calm) return;
    const t = setInterval(() => { const l = Math.max(0, b.end - Date.now()); setLeft(l); if (l <= 0) { clearInterval(t); if (S.screen === "bubbles" && S.bub === b) finish(); } }, 250);
    onLeave(() => clearInterval(t)); return () => clearInterval(t);
  }, [b]);
  useEffect(() => { if (s.spokenQ !== q) { s.spokenQ = q; setTimeout(() => sayAloud(E.plain(q.say || q.text)), 200); } }, [q]);
  const tap = (o: string, el: HTMLElement) => {
    if (popped) return;
    if (E.isCorrect(q, o)) {
      setPopped(o); SFX.pop(b.score % 8); burstAt(el, 10); b.score++;
      // A bubble that was only popped after a wrong tap was already counted as missed: it must not also count as right.
      const p = prog(b.tid); p.last = Date.now();
      if (!(q as any).missed) { p.seen++; p.right++; pushRecent(p, true); queue({ type: "attempt", topicId: b.tid, kind: "bubble", correct: true, firstTry: true, level: p.level }); }
      saveProg(b.tid);
      if (b.score % 3 === 0) { b.starsWon++; addStars(1); }
      setTimeout(() => { if (S.screen !== "bubbles" || S.bub !== b) return; if (b.calm && b.score >= CALM_GOAL) { finish(); return; } nextBub(); setPopped(null); touch(); }, 380);
    } else {
      setWrong(o); SFX.no(); setTimeout(() => setWrong(null), 400);
      // Count the first wrong tap on a bubble as a miss, so popping at random cannot lift a topic towards "mastered".
      if (!(q as any).missed) { (q as any).missed = true; const p = prog(b.tid); p.seen++; pushRecent(p, false); p.last = Date.now(); saveProg(b.tid); queue({ type: "attempt", topicId: b.tid, kind: "bubble", correct: false, firstTry: false, level: p.level }); }
    }
  };
  const pct = b.calm ? (b.score / CALM_GOAL) * 100 : (left / (SECS * 1000)) * 100;
  return <main><Card className="stage">
    <div className="bub-top">
      <div className={`timer ${b.calm ? "calm" : left < 10000 ? "low" : ""}`} aria-label={b.calm ? `${b.score} of ${CALM_GOAL} pops` : "time left"}><i style={{ width: `${pct}%` }} /></div>
      <span className="score" aria-live="polite">{b.score}</span>
      <button className="btn soft" aria-pressed={!b.calm} onClick={() => { updateChild({ settings: { ...S.child!.settings, calm: !b.calm } }); startBubbles(b.mixed); }}>{b.calm ? "Calm: no timer" : "Race: 60 s"}</button>
    </div>
    <div className="qrow">{canSpeak && <button className="round saybtn" aria-label="Read the question" onClick={() => speak(E.plain(q.say || q.text), s.child?.board)}><Icon name="speak" /></button>}<Html as="p" className="qtext" html={q.text} /></div>
    {q.viz && <Html key={String(b.score)} className="vizbox sm" html={q.viz} onClick={tapCount} />}
    <div className="pond">{b.opts.map((o, i) => <button key={b.score + "-" + o} className={`bb ${popped === o ? "popped" : ""} ${wrong === o ? "no" : ""}`} style={{ ["--i" as any]: i, ["--c" as any]: CS[i] }} onClick={(e) => tap(o, e.currentTarget)}>
      <Html as="span" html={E.bubbleLabel(o, q)} /></button>)}</div>
    <p className="note" style={{ textAlign: "center" }}>{b.mixed ? (b.label ? `${b.label}.` : `Mixed ${E.board().gradeWord} ${s.child!.grade} questions.`) : E.topicTitle(E.topic(b.topic!)!) + "."} {b.calm ? `Pop ${CALM_GOAL} right answers at your own speed.` : "Pop as many as you can in 60 seconds."} Every 3 pops wins a star.</p>
  </Card></main>;
}
